import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RefreshCw, X, Plus } from 'lucide-react';
import { format } from 'date-fns';
import AdminCreateBooking from './AdminCreateBooking';

const AdminBookings = ({
  allBookings,
  refundedBookings,
  bookingsLoading,
  showRefundedTab,
  setShowRefundedTab,
  showRefundModal,
  setShowRefundModal,
  refundingBooking,
  refundReason,
  setRefundReason,
  refundAmount,
  setRefundAmount,
  isFullRefund,
  setIsFullRefund,
  processingRefund,
  onRefresh,
  onOpenRefundModal,
  onProcessRefund,
  onClose
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleBookingCreated = () => {
    onRefresh(); // Refresh the bookings list
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-background-paper border border-white/10 rounded-sm p-8 mb-12"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl font-bold">
          MANAGE <span className="text-primary">BOOKINGS</span>
        </h2>
        <div className="flex items-center gap-4">
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCreateModal(true)}
            className="bg-primary hover:bg-primary/80"
            data-testid="open-create-booking-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Booking
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={bookingsLoading}
            data-testid="refresh-bookings-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${bookingsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="ghost" onClick={onClose} data-testid="close-bookings-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-white/10">
        <button
          onClick={() => setShowRefundedTab(false)}
          className={`pb-3 px-4 font-bold transition-colors ${
            !showRefundedTab 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-neutral-400 hover:text-white'
          }`}
          data-testid="active-bookings-tab"
        >
          Active Bookings ({allBookings.length})
        </button>
        <button
          onClick={() => setShowRefundedTab(true)}
          className={`pb-3 px-4 font-bold transition-colors ${
            showRefundedTab 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-neutral-400 hover:text-white'
          }`}
          data-testid="refunded-bookings-tab"
        >
          Refunded ({refundedBookings.length})
        </button>
      </div>

      {bookingsLoading ? (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-neutral-400">Loading bookings...</p>
        </div>
      ) : !showRefundedTab ? (
        /* Active Bookings */
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {allBookings.length === 0 ? (
            <p className="text-neutral-400 text-center py-8">No active bookings found</p>
          ) : (
            allBookings.map((booking, index) => (
              <div
                key={booking.booking_id}
                className="flex justify-between items-center p-4 bg-background rounded-sm border border-white/5 hover:border-white/10 transition-colors"
                data-testid={`booking-row-${index}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="font-bold">{booking.class_title}</p>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      booking.payment_method === 'paypal' 
                        ? 'bg-blue-500/20 text-blue-400' 
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {booking.payment_method === 'paypal' ? 'PayPal' : 'Cash'}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-400">
                    {booking.user_name} ({booking.user_email})
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    Class: {format(new Date(booking.class_datetime), 'MMM d, yyyy h:mm a')} • 
                    Booked: {format(new Date(booking.booking_date), 'MMM d, yyyy')}
                    {booking.discount_code && (
                      <span className="ml-2 text-primary">
                        Discount: {booking.discount_code} (-£{booking.discount_amount?.toFixed(2)})
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-bold text-lg">£{booking.final_price?.toFixed(2)}</p>
                    {booking.discount_amount > 0 && (
                      <p className="text-xs text-neutral-500 line-through">£{booking.original_price?.toFixed(2)}</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-error text-error hover:bg-error hover:text-white"
                    onClick={() => onOpenRefundModal(booking)}
                    data-testid={`refund-btn-${index}`}
                  >
                    Refund
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* Refunded Bookings */
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {refundedBookings.length === 0 ? (
            <p className="text-neutral-400 text-center py-8">No refunded bookings found</p>
          ) : (
            refundedBookings.map((booking, index) => (
              <div
                key={booking.booking_id}
                className="flex justify-between items-center p-4 bg-background rounded-sm border border-white/5"
                data-testid={`refunded-row-${index}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="font-bold">{booking.class_title}</p>
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-error/20 text-error">
                      REFUNDED
                    </span>
                  </div>
                  <p className="text-sm text-neutral-400">
                    {booking.user_name} ({booking.user_email})
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    Refunded: {booking.refunded_at && format(new Date(booking.refunded_at), 'MMM d, yyyy h:mm a')}
                  </p>
                  <p className="text-xs text-neutral-400 mt-1 italic">
                    Reason: {booking.refund_reason}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg text-error">-£{booking.refund_amount?.toFixed(2)}</p>
                  <p className="text-xs text-neutral-500">
                    Original: £{booking.original_price?.toFixed(2)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && refundingBooking && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background-paper border border-white/10 rounded-sm p-8 max-w-md w-full"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-heading text-xl font-bold">
                PROCESS <span className="text-error">REFUND</span>
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowRefundModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="bg-background p-4 rounded-sm mb-6">
              <p className="font-bold">{refundingBooking.class_title}</p>
              <p className="text-sm text-neutral-400">{refundingBooking.user_name}</p>
              <p className="text-sm text-neutral-400">{refundingBooking.user_email}</p>
              <div className="flex justify-between mt-2 pt-2 border-t border-white/10">
                <span className="text-neutral-400">Amount Paid:</span>
                <span className="font-bold">£{refundingBooking.final_price?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Payment Method:</span>
                <span className="capitalize">{refundingBooking.payment_method}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="refund-reason" className="text-error">Refund Reason (Required)</Label>
                <Textarea
                  id="refund-reason"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="e.g., Class cancelled, Customer request..."
                  className="bg-background border-white/10 mt-1"
                  rows={3}
                  data-testid="refund-reason-input"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={isFullRefund}
                    onChange={() => setIsFullRefund(true)}
                    className="w-4 h-4 accent-primary"
                  />
                  <span>Full Refund (£{refundingBooking.final_price?.toFixed(2)})</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!isFullRefund}
                    onChange={() => setIsFullRefund(false)}
                    className="w-4 h-4 accent-primary"
                  />
                  <span>Partial Refund</span>
                </label>
              </div>

              {!isFullRefund && (
                <div>
                  <Label htmlFor="refund-amount">Refund Amount (£)</Label>
                  <Input
                    id="refund-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={refundingBooking.final_price}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="bg-background border-white/10 mt-1"
                    data-testid="refund-amount-input"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-4 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowRefundModal(false)}
                disabled={processingRefund}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-error hover:bg-error/80"
                onClick={onProcessRefund}
                disabled={processingRefund || !refundReason.trim()}
                data-testid="confirm-refund-btn"
              >
                {processingRefund ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Process Refund'
                )}
              </Button>
            </div>

            {refundingBooking.payment_method === 'paypal' && (
              <p className="text-xs text-neutral-500 mt-4 text-center">
                PayPal refunds typically take 5-10 business days to process.
              </p>
            )}
          </motion.div>
        </div>
      )}

      {/* Create Booking Modal */}
      {showCreateModal && (
        <AdminCreateBooking
          onClose={() => setShowCreateModal(false)}
          onBookingCreated={handleBookingCreated}
        />
      )}
    </motion.div>
  );
};

export default AdminBookings;
